import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { AuthBllService } from "../../features/auth/auth-bll.service";
import { take, map } from "rxjs/operators";
import { StorageService } from "../services/storage.service";

/**
 * Functional route guard that prevents navigation to protected routes
 * unless the user is authenticated.
 *
 * Uses the AuthBllService’s `isAuthenticated$` observable to check auth state.
 * If unauthenticated, redirects to `/auth/init`.
 */
export const authGuard: CanActivateFn = (route, state) => {
  // Retrieve the AuthBllService and Router via Angular’s inject API
  const authService = inject(AuthBllService);
  const router = inject(Router);
  const storage = inject(StorageService);

  return authService.isAuthenticated$.pipe(
    // Only take the first emission to complete the guard check immediately
    take(1),
    map((isAuth) => {
      if (!isAuth) {
        // Not authenticated → navigate to login/init page
        storage.clearAll();

        const currentUrl = state.url;

        if (currentUrl.startsWith("/reg/select-ticket")) {
          // Remove all query params from the URL
          const urlTree = router.parseUrl(currentUrl);
          // Save ref_source if present
          const queryParams = urlTree.queryParams;
          if (queryParams["ref_source"]) {
            storage.saveRefSource(queryParams["ref_source"]);
          }
          // Extract only the path without query params
          const urlWithoutQuery = authService.stripQueryParams(urlTree);

          // Save clean URL (no query params)
          storage.saveState(urlWithoutQuery);
        } else {
          storage.saveState(currentUrl)
        }
        // Redirect to /auth/init
        router.navigate(["/auth/init"]);
      }
      // Return true if authenticated, false otherwise
      return isAuth;
    })
  );
};
